/**
 * Delivery staff inbox — take/claim lock, POD confirm (3 options + exception).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CloseIcon from '@mui/icons-material/Close';
import DeliveryStaffShell from '../components/DeliveryStaffShell';
import {
  deliveryStaffApi,
  getStaffSession,
  staffAppPath,
} from '../lib/deliveryStaffApi';
import { initAudioUnlock, playOrderAlertChime } from '../lib/orderAlerts';

const POD_EXCEPTION_OPTIONS = [
  { value: 'resident_refused', label: 'Resident refused / no answer' },
  { value: 'no_code_in_app', label: 'Resident has no code in app' },
  { value: 'left_agreed', label: 'Left as agreed with resident' },
  { value: 'wrong_apartment_returned', label: 'Wrong apartment — returned' },
  { value: 'other', label: 'Other (note required)' },
];

function parseErr(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) {
    return detail.message;
  }
  return err?.message || fallback;
}

export default function DeliveryStaffOrders() {
  const navigate = useNavigate();
  const session = getStaffSession();
  const myId = session.staffId != null ? String(session.staffId) : null;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);

  const [podOrder, setPodOrder] = useState(null);
  const [podLoading, setPodLoading] = useState(false);
  const [podOptions, setPodOptions] = useState([]);
  const [podFails, setPodFails] = useState(0);
  const [podMaxFails, setPodMaxFails] = useState(3);
  const [podException, setPodException] = useState('');
  const [podNote, setPodNote] = useState('');
  const [podError, setPodError] = useState('');
  const [podSubmitting, setPodSubmitting] = useState(false);
  /** Phone-only pick ticks: { [orderId]: { [itemId]: true } } */
  const [pickedMap, setPickedMap] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  const lastIdsRef = useRef(new Set());

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const list = await deliveryStaffApi.listOrders({
          q: q.trim() || undefined,
        });
        const ids = new Set((list || []).map((o) => o.id));
        if (lastIdsRef.current.size > 0) {
          let newer = 0;
          ids.forEach((id) => {
            if (!lastIdsRef.current.has(id)) newer += 1;
          });
          if (newer > 0) {
            playOrderAlertChime();
            if (
              typeof Notification !== 'undefined' &&
              Notification.permission === 'granted'
            ) {
              try {
                new Notification('GoSnel Delivery', {
                  body: newer === 1 ? 'New order ready' : `${newer} new orders`,
                  icon: '/icon-192.png',
                  tag: 'staff-order',
                });
              } catch {
                /* ignore */
              }
            }
          }
        }
        lastIdsRef.current = ids;
        setOrders(list || []);
        setError('');
      } catch (e) {
        setError(parseErr(e, 'Failed to load orders'));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [q]
  );

  useEffect(() => {
    initAudioUnlock();
    try {
      localStorage.setItem('delivery_staff_last_role', 'staff');
    } catch {
      /* ignore */
    }
    load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => load({ silent: true }), 10000);
    return () => clearInterval(t);
  }, [load]);

  const isOnTheWay = (o) => {
    const s = String(o?.status || '').toLowerCase();
    return s === 'sent' || s === 'on_the_way' || s === 'on-the-way';
  };

  const normId = (v) => (v != null ? String(v).toLowerCase() : null);
  const myNorm = normId(myId);

  const isMine = (o) => {
    const takenId = normId(o?.taken_by_staff_id);
    return !!(takenId && myNorm && takenId === myNorm);
  };

  const myActiveOrder = (orders || []).find((o) => isMine(o)) || null;
  const holdingOther = (o) => !!(myActiveOrder && myActiveOrder.id !== o.id);

  const pickableItems = (o) =>
    (Array.isArray(o?.items) ? o.items : []).filter((it) => !it?.unavailable);

  const allPicked = (o) => {
    const items = pickableItems(o);
    if (!items.length) return true;
    const ticks = pickedMap[o.id] || {};
    return items.every((it) => ticks[it.id]);
  };

  const togglePicked = (orderId, itemId) => {
    setPickedMap((prev) => {
      const cur = { ...(prev[orderId] || {}) };
      if (cur[itemId]) delete cur[itemId];
      else cur[itemId] = true;
      return { ...prev, [orderId]: cur };
    });
  };

  const clearPicked = (orderId) => {
    setPickedMap((prev) => {
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  };

  const onTake = async (o) => {
    if (holdingOther(o)) {
      setError(
        'You already have an order. Finish or Release it before taking another.'
      );
      return;
    }
    setBusyId(o.id);
    try {
      await deliveryStaffApi.takeOrder(o.id, o.kind);
      await load({ silent: true });
    } catch (e) {
      const status = e?.response?.status;
      setError(
        parseErr(
          e,
          status === 409
            ? 'Already taken — or finish / Release your current order first'
            : 'Could not take order'
        )
      );
      await load({ silent: true });
    } finally {
      setBusyId(null);
    }
  };

  const onOnTheWay = async (o) => {
    if (!allPicked(o)) {
      setError('Check every item as you pack, then tap On the way.');
      return;
    }
    setBusyId(o.id);
    try {
      await deliveryStaffApi.onTheWay(o.id, o.kind);
      await load({ silent: true });
    } catch (e) {
      setError(parseErr(e, 'Could not mark on the way'));
    } finally {
      setBusyId(null);
    }
  };

  const onRelease = async (o) => {
    setBusyId(o.id);
    try {
      await deliveryStaffApi.releaseOrder(o.id, o.kind);
      clearPicked(o.id);
      await load({ silent: true });
    } catch (e) {
      setError(parseErr(e, 'Could not release'));
    } finally {
      setBusyId(null);
    }
  };

  const resetPod = () => {
    setPodOrder(null);
    setPodOptions([]);
    setPodFails(0);
    setPodException('');
    setPodNote('');
    setPodError('');
    setPodSubmitting(false);
    setPodLoading(false);
  };

  const openPod = async (o) => {
    if (!isMine(o)) {
      if (!o.taken_by_staff_id) {
        setError('Take & pack the order first, then Confirm delivery');
      } else {
        setError(o.taken_label || 'Taken by another staff');
      }
      return;
    }
    if (!allPicked(o)) {
      setError('Check every item as you pack before Confirm delivery.');
      return;
    }
    setPodOrder(o);
    setPodLoading(true);
    setPodError('');
    setPodException('');
    setPodNote('');
    try {
      const challenge = await deliveryStaffApi.getPodChallenge(o.id, o.kind);
      setPodOptions(challenge?.options || []);
      setPodFails(challenge?.fails || 0);
      setPodMaxFails(challenge?.max_fails || 3);
    } catch (e) {
      setPodError(parseErr(e, 'Could not load codes'));
    } finally {
      setPodLoading(false);
    }
  };

  const submitPodCode = async (code) => {
    if (!podOrder || podSubmitting) return;
    setPodSubmitting(true);
    setPodError('');
    try {
      const doneId = podOrder.id;
      await deliveryStaffApi.confirmDelivery(podOrder.id, {
        code,
        orderKind: podOrder.kind,
      });
      clearPicked(doneId);
      resetPod();
      await load({ silent: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        setPodFails(detail.fails || podFails + 1);
        setPodError(
          detail.locked
            ? 'Too many wrong attempts — use an exception reason'
            : `Wrong code (${detail.fails}/${detail.max_fails || podMaxFails})`
        );
        try {
          const challenge = await deliveryStaffApi.getPodChallenge(
            podOrder.id,
            podOrder.kind
          );
          setPodOptions(challenge?.options || []);
        } catch {
          /* keep */
        }
      } else {
        setPodError(parseErr(e, 'Confirm failed'));
      }
    } finally {
      setPodSubmitting(false);
    }
  };

  const submitPodException = async () => {
    if (!podOrder || !podException || podSubmitting) return;
    if (podException === 'other' && !podNote.trim()) {
      setPodError('Please add a short note for Other');
      return;
    }
    setPodSubmitting(true);
    try {
      const doneId = podOrder.id;
      await deliveryStaffApi.confirmDelivery(podOrder.id, {
        exceptionReason: podException,
        note: podNote.trim() || undefined,
        orderKind: podOrder.kind,
      });
      clearPicked(doneId);
      resetPod();
      await load({ silent: true });
    } catch (e) {
      setPodError(parseErr(e, 'Confirm failed'));
    } finally {
      setPodSubmitting(false);
    }
  };

  return (
    <DeliveryStaffShell title="Delivery Orders">
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            size="small"
            placeholder="Search building / apt / address"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ flex: 1, minWidth: 160 }}
          />
          <IconButton onClick={() => load()} disabled={loading} aria-label="Refresh">
            <RefreshIcon
              sx={{
                animation: loading ? 'spin 0.8s linear infinite' : 'none',
                '@keyframes spin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>
          <Button
            size="small"
            variant="outlined"
            startIcon={<NotificationsActiveIcon />}
            onClick={() =>
              navigate(staffAppPath('/delivery-staff/notifications-help'))
            }
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Order alerts
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading && orders.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#0f766e' }} />
          </Box>
        ) : orders.length === 0 ? (
          <PaperEmpty />
        ) : (
          <Stack spacing={1.5}>
            {myActiveOrder && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                You can hold only one order. Finish or Release your current
                order before taking another.
              </Alert>
            )}
            {[...orders]
              .sort((a, b) => {
                const am = isMine(a) ? 0 : 1;
                const bm = isMine(b) ? 0 : 1;
                if (am !== bm) return am - bm;
                return 0;
              })
              .map((o) => {
              const takenId = normId(o.taken_by_staff_id);
              const mine = isMine(o);
              const takenByOther = !!(takenId && !mine);
              const onWay = isOnTheWay(o);
              const items = Array.isArray(o.items) ? o.items : [];
              const ticks = pickedMap[o.id] || {};
              const packed = allPicked(o);
              const blockedByHold = !takenId && holdingOther(o);
              return (
                <Card
                  key={`${o.kind}-${o.id}`}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: mine ? '#0f766e' : undefined,
                    boxShadow: mine ? '0 0 0 1px #0f766e33' : undefined,
                  }}
                >
                  <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={1}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={800}>
                          {o.kind === 'tower'
                            ? [o.building_name, o.apartment]
                                .filter(Boolean)
                                .join(' · ') || 'Neighbour order'
                            : o.delivery_address || 'Order'}
                        </Typography>
                        {o.resident_name && (
                          <Typography variant="body2" color="text.secondary">
                            {o.resident_name}
                          </Typography>
                        )}
                        {(() => {
                          const address =
                            o.delivery_address ||
                            (o.kind === 'tower'
                              ? [o.building_name, o.apartment ? `Apt ${o.apartment}` : null]
                                  .filter(Boolean)
                                  .join(', ')
                              : null) ||
                            null;
                          const mapsUrl =
                            o.user_maps_url ||
                            (address
                              ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
                              : null);
                          if (!address && !mapsUrl) return null;
                          return (
                            <Box sx={{ mt: 0.5 }}>
                              {address ? (
                                <Typography
                                  variant="body2"
                                  sx={{ color: 'text.secondary', lineHeight: 1.35 }}
                                >
                                  Address: {address}
                                </Typography>
                              ) : null}
                              {mapsUrl ? (
                                <Button
                                  size="small"
                                  href={mapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    mt: 0.25,
                                    px: 0,
                                    minWidth: 0,
                                    textTransform: 'none',
                                    color: '#0f766e',
                                    fontWeight: 700,
                                  }}
                                >
                                  Open in Google Maps
                                </Button>
                              ) : null}
                            </Box>
                          );
                        })()}
                        <Typography variant="caption" color="text.secondary">
                          {o.status}
                          {o.total != null
                            ? ` · ${Number(o.total).toFixed(2)}${
                                o.currency ? ` ${o.currency}` : ''
                              }`
                            : ''}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={o.kind === 'tower' ? 'Neighbour' : 'Order'}
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>

                    {(o.taken_label || o.taken_by_name) && (
                      <Chip
                        size="small"
                        label={o.taken_label || `Taken by ${o.taken_by_name}`}
                        sx={{
                          mt: 1,
                          fontSize: '11px',
                          borderColor: mine ? '#0f766e' : '#6366f1',
                          color: mine ? '#0f766e' : '#4338ca',
                          bgcolor: mine ? '#ccfbf1' : '#eef2ff',
                        }}
                        variant="outlined"
                      />
                    )}

                    <Divider sx={{ my: 1.25 }} />
                    <Typography
                      variant="caption"
                      fontWeight={800}
                      sx={{ display: 'block', mb: 0.5, color: '#0f766e' }}
                    >
                      {mine ? 'Pack checklist — tick as you pick' : 'Order items'}
                    </Typography>
                    {items.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No line items on this order.
                      </Typography>
                    ) : (
                      <Stack spacing={0.75} sx={{ mb: 0.5 }}>
                        {items.map((it) => {
                          const label = `${it.name || 'Item'}${
                            it.quantity && Number(it.quantity) !== 1
                              ? ` × ${it.quantity}`
                              : ''
                          }`;
                          const thumb = it.image_url ? (
                            <Box
                              component="img"
                              src={it.image_url}
                              alt=""
                              loading="lazy"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewImage({
                                  url: it.image_url,
                                  label,
                                });
                              }}
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 1.25,
                                objectFit: 'cover',
                                flexShrink: 0,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: '#f1f5f9',
                                opacity: ticks[it.id] || it.unavailable ? 0.55 : 1,
                                cursor: 'zoom-in',
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 1.25,
                                flexShrink: 0,
                                bgcolor: '#f1f5f9',
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'grid',
                                placeItems: 'center',
                                color: 'text.disabled',
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              —
                            </Box>
                          );
                          if (mine && !it.unavailable) {
                            return (
                              <Stack
                                key={it.id}
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                                sx={{ py: 0.25 }}
                              >
                                {thumb}
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={!!ticks[it.id]}
                                      onChange={() => togglePicked(o.id, it.id)}
                                      sx={{
                                        color: '#0f766e',
                                        '&.Mui-checked': { color: '#0f766e' },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        textDecoration: ticks[it.id]
                                          ? 'line-through'
                                          : 'none',
                                        color: ticks[it.id]
                                          ? 'text.secondary'
                                          : 'text.primary',
                                      }}
                                    >
                                      {label}
                                      {it.notes ? ` — ${it.notes}` : ''}
                                    </Typography>
                                  }
                                  sx={{ m: 0, alignItems: 'flex-start', flex: 1 }}
                                />
                              </Stack>
                            );
                          }
                          return (
                            <Stack
                              key={it.id}
                              direction="row"
                              spacing={1}
                              alignItems="flex-start"
                              sx={{ py: 0.25, pl: 0.25 }}
                            >
                              {thumb}
                              <Typography
                                variant="body2"
                                sx={{
                                  pt: 1,
                                  color: it.unavailable
                                    ? 'text.disabled'
                                    : 'text.primary',
                                  textDecoration: it.unavailable
                                    ? 'line-through'
                                    : 'none',
                                }}
                              >
                                {label}
                                {it.unavailable ? ' (unavailable)' : ''}
                                {it.notes ? ` — ${it.notes}` : ''}
                              </Typography>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                    {o.notes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        Note: {o.notes}
                      </Typography>
                    )}

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 1.5 }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {!takenId && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busyId === o.id || blockedByHold}
                          onClick={() => onTake(o)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: '#0f766e',
                            '&:hover': { bgcolor: '#0d9488' },
                          }}
                        >
                          Take & pack
                        </Button>
                      )}
                      {takenByOther && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            minWidth: 88,
                          }}
                        >
                          Taken
                        </Button>
                      )}
                      {mine && !onWay && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busyId === o.id || !packed}
                          onClick={() => onOnTheWay(o)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: '#2563eb',
                            '&:hover': { bgcolor: '#1d4ed8' },
                          }}
                        >
                          On the way
                        </Button>
                      )}
                      {mine && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busyId === o.id}
                          onClick={() => onRelease(o)}
                          sx={{ textTransform: 'none' }}
                        >
                          Release
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={!mine || busyId === o.id || !packed}
                        onClick={() => openPod(o)}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Confirm delivery
                      </Button>
                    </Stack>
                    {!takenId && !blockedByHold && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        View items above, then Take & pack to tick each item as
                        you pick. Confirm stays locked until you take it.
                      </Typography>
                    )}
                    {blockedByHold && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        Finish or Release your current order before taking this
                        one.
                      </Typography>
                    )}
                    {mine && !packed && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ display: 'block', mt: 1, fontWeight: 700 }}
                      >
                        Tick every item as you pack before On the way / Confirm.
                      </Typography>
                    )}
                    {takenByOther && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        {o.taken_label ||
                          'Another delivery worker has this order — you cannot take it.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Container>

      <Dialog
        open={!!podOrder}
        onClose={() => !podSubmitting && resetPod()}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm delivery</DialogTitle>
        <DialogContent>
          {podLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Ask the resident for their 2-digit code, then tap the matching
                option.
              </Typography>
              {podError && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {podError}
                </Alert>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 1.25,
                  mb: 2,
                }}
              >
                {(podOptions || []).map((opt) => (
                  <Button
                    key={opt}
                    variant="contained"
                    disabled={podSubmitting || podFails >= podMaxFails}
                    onClick={() => submitPodCode(opt)}
                    sx={{
                      py: 1.75,
                      fontSize: '1.25rem',
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      bgcolor: '#0f766e',
                      '&:hover': { bgcolor: '#0d9488' },
                    }}
                  >
                    {String(opt).padStart(2, '0').slice(-2)}
                  </Button>
                ))}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1}
              >
                Or log an exception if the resident has no code:
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Exception reason</InputLabel>
                <Select
                  value={podException}
                  label="Exception reason"
                  onChange={(e) => setPodException(e.target.value)}
                  disabled={podSubmitting}
                >
                  {POD_EXCEPTION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(podException === 'other' || podException) && (
                <TextField
                  fullWidth
                  size="small"
                  label={
                    podException === 'other' ? 'Note (required)' : 'Note (optional)'
                  }
                  value={podNote}
                  onChange={(e) => setPodNote(e.target.value)}
                  disabled={podSubmitting}
                  sx={{ mb: 1 }}
                />
              )}
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                disabled={!podException || podSubmitting}
                onClick={submitPodException}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {podSubmitting ? 'Saving…' : 'Confirm with exception'}
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => !podSubmitting && resetPod()}
            disabled={podSubmitting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(previewImage?.url)}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#0f172a', m: 1, maxHeight: '92vh' },
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontWeight: 800, pr: 6 }}>
          {previewImage?.label || 'Item photo'}
          <IconButton
            aria-label="Close"
            onClick={() => setPreviewImage(null)}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 1, sm: 2 },
            minHeight: 240,
          }}
        >
          {previewImage?.url ? (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.label || 'Item photo'}
              sx={{
                maxWidth: '100%',
                maxHeight: '78vh',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            href={previewImage?.url || undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!previewImage?.url}
            sx={{ textTransform: 'none', color: '#99f6e4' }}
          >
            Open original
          </Button>
          <Button
            onClick={() => setPreviewImage(null)}
            sx={{ textTransform: 'none', color: '#fff' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DeliveryStaffShell>
  );
}

function PaperEmpty() {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 6,
        px: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: '#fff',
      }}
    >
      <Typography fontWeight={700} sx={{ mb: 0.5 }}>
        No active delivery orders
      </Typography>
      <Typography variant="body2" color="text.secondary">
        New orders appear here when packing / on the way. Keep this page open
        or enable Order alerts for a chime.
      </Typography>
    </Box>
  );
}
